import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Star, Home as HomeIcon, Wallet, ClipboardCheck, Hammer } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Megward Construction — concept site. Warm, accessible residential design-build
  identity: navy + terracotta on cream, real house-package pricing tiers and real
  in-progress project photography. Deliberately not editorial/corporate like ACT.
*/

const NAVY = "#1E3A5F";
const TERRACOTTA = "#D97757";
const CREAM = "#FBF6EF";
const INK = "#1F1B16";

const FB = "https://www.facebook.com/megwardconstructionandsupplies/";
const NAME = "Megward Construction";
const SLUG = "megward-construction-concept";
const P = "/construction/megward";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function MegwardConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["House Packages", "#packages"], ["Our Work", "#work"], ["Process", "#process"], ["Financing", "#financing"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Quicksand', sans-serif" }} data-testid="megward-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&family=Poppins:wght@600;700&display=swap');
        .mg-head { font-family: 'Poppins', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#1F1B16] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="mg-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${TERRACOTTA}` }} />
            <span className="mg-head text-lg" style={{ color: NAVY }}>Megward Construction</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold" style={{ color: `${INK}CC` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="mg-header-cta" className="hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-bold text-white" style={{ background: TERRACOTTA }}>
            Start Your Build
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mg-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-semibold" style={{ color: INK }}>{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="mg-mobile-cta" className="block mt-2 text-center rounded-full px-5 py-3 text-sm font-bold text-white" style={{ background: TERRACOTTA }}>Start Your Build</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end" data-testid="mg-hero">
        <img src={`${P}/project-nearcomplete.jpg`} alt="A home built by Megward Construction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${NAVY}22 0%, ${NAVY}CC 75%, ${NAVY}F2 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>Bacolod City, Negros Occidental</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white mg-head font-bold">From Start to Finish, We Build Your Home.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              Planning, design, house construction, renovation and condominium fit-out — with site visits, consultation and project estimating built into every build.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="mg-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-bold text-white" style={{ background: TERRACOTTA }}>Start Your Build</a>
              <a href="#packages" data-testid="mg-hero-packages" className="rounded-full border border-white/50 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">See House Packages</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="mg-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: TERRACOTTA }} /> 5.0 Google Rating · 16 Reviews · Bacolod City
            </p>
          </motion.div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>House Packages</span>
          <h2 className="mt-3 text-4xl sm:text-5xl mg-head font-bold leading-snug" style={{ color: NAVY }}>Pick a Finish, We Handle the Rest</h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: `${INK}99` }}>Every package includes a complete set of signed &amp; sealed plans and processed building/occupancy permits.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { img: `${P}/package-bare.jpg`, tier: "Bare Finish", price: "₱19,000", note: "per sqm · exposed ceiling, finished concrete walls & flooring" },
            { img: `${P}/package-standard.jpg`, tier: "Standard Finish", price: "₱27,000", note: "per sqm · painted walls, tiled flooring, kitchen & bath fixtures" },
            { img: `${P}/package-elegant.jpg`, tier: "Elegant Finish", price: "₱32,000", note: "per sqm · premium kitchen & bath, ceiling design, high-quality doors & windows" },
          ].map((p) => (
            <motion.div key={p.tier} {...fadeUp} data-testid="mg-package-card" className="rounded-2xl overflow-hidden bg-white" style={{ boxShadow: "0 10px 30px rgba(30,58,95,0.08)" }}>
              <img src={p.img} alt={p.tier} className="w-full h-52 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-xl font-bold mg-head" style={{ color: NAVY }}>{p.tier}</h3>
                <p className="mt-2 text-2xl font-bold" style={{ color: TERRACOTTA }}>{p.price}<span className="text-sm font-medium" style={{ color: `${INK}80` }}> /sqm</span></p>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{p.note}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OUR WORK */}
      <section id="work" className="py-20 md:py-28" style={{ background: NAVY }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>Real Builds, Real Progress</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white mg-head font-bold leading-snug">On-Site With Our Crew</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { img: `${P}/project-nearcomplete.jpg`, label: "Two-storey residence, near completion" },
              { img: `${P}/project-framing.jpg`, label: "Structural framing in progress" },
              { img: `${P}/project-tilework.jpg`, label: "Interior tile work" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="mg-work-item" className="rounded-xl overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-64 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>How It Works</span>
          <h2 className="mt-3 text-4xl sm:text-5xl mg-head font-bold leading-snug" style={{ color: NAVY }}>Build Process</h2>
        </motion.div>
        <div className="grid sm:grid-cols-4 gap-8">
          {[
            [ClipboardCheck, "Consultation", "Site visit and consultation to understand your lot, budget and needs."],
            [HomeIcon, "Design", "Architectural/engineering design tailored to the site and package chosen."],
            [Hammer, "Construction", "Overall construction, from foundation to renovation or condo fit-out."],
            [Wallet, "Permits & Turnover", "Building/occupancy permit processing, then turnover."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: TERRACOTTA }} />
              <h3 className="mt-4 text-lg font-bold mg-head" style={{ color: NAVY }}>{t}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINANCING */}
      <section id="financing" className="py-16 md:py-20" style={{ background: "#F0E4D6" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>Financing</span>
            <h2 className="mt-3 text-3xl sm:text-4xl mg-head font-bold" style={{ color: NAVY }}>Build Now, Pay Later</h2>
            <p className="mt-4 text-base max-w-2xl mx-auto leading-relaxed" style={{ color: `${INK}90` }}>We offer a bank-financing route so you can start your build now and pay over time. Ask our team for current requirements and terms.</p>
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TERRACOTTA }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl mg-head font-bold leading-snug" style={{ color: NAVY }}>Start Your Build</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: TERRACOTTA }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>2796 Hilado St., Bacolod City, Negros Occidental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: TERRACOTTA }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639108242224" className="hover:underline">0910 824 2224</a><span className="block text-sm">(034) 467-8120</span></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: TERRACOTTA }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><a href="mailto:megwardconstruction@gmail.com" className="hover:underline">megwardconstruction@gmail.com</a></div>
              </li>
              <li className="flex items-start gap-4 min-w-0">
                <Facebook size={20} style={{ color: TERRACOTTA }} className="mt-0.5 shrink-0" />
                <div className="min-w-0"><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="mg-facebook-link" className="hover:underline break-all">facebook.com/megwardconstructionandsupplies</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="mg"
              accent={TERRACOTTA}
              accentText="#FFFFFF"
              heading="Start Your Build"
              subheading="Tell us about your lot and the finish level you're considering — we'll follow up to schedule a site visit."
              projectTypes={["New House Build", "Renovation", "Condominium Fit-Out", "Other"]}
              phone="+639108242224"
              phoneDisplay="0910 824 2224"
              messengerUrl={FB}
              email="megwardconstruction@gmail.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: NAVY, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${TERRACOTTA}` }} />
              <span className="text-white text-lg mg-head font-bold">Megward Construction</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Residential design-build in Bacolod City — from consultation through permits and turnover.</p>
          </div>
          <div>
            <div className="text-white text-sm font-bold mb-3">House Packages</div>
            <ul className="text-sm space-y-2">
              <li>Bare Finish — ₱19,000/sqm</li>
              <li>Standard Finish — ₱27,000/sqm</li>
              <li>Elegant Finish — ₱32,000/sqm</li>
            </ul>
          </div>
          <div>
            <div className="text-white text-sm font-bold mb-3">Contact</div>
            <p className="text-sm">2796 Hilado St.,<br />Bacolod City, Negros Occidental</p>
            <p className="text-sm mt-2">0910 824 2224</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Pricing, packages and financing terms should always be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: TERRACOTTA }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
